import { Injectable } from '@angular/core';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { AppNotificationUtilClass, Plani8DataService } from '@monorepo/services';
import { switchMap, map, catchError, take, finalize } from 'rxjs/operators';
import { of, from } from 'rxjs';
import * as FactoryShiftActions from './factory-shift.actions';
import { FactoryShiftFacade } from './factory-shift.facade';
import { TFactoryShiftInfoActionType } from './factory-shift.models';

@Injectable({ providedIn: 'root' })
export class FactoryShiftEffects {
  constructor(
    private actions$: Actions,
    private plani8DataService: Plani8DataService,
    private factoryShiftFacade: FactoryShiftFacade,
    private appNotificationUtilClass: AppNotificationUtilClass,
  ) {}

  getFactoryShiftInfo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TFactoryShiftInfoActionType.GetFactoryShiftInfo),
      switchMap((action: FactoryShiftActions.GetFactoryShiftInfo) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.plani8DataService.fetchFactoryShifts(action.factoryId, action.payload).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          map((res) => new FactoryShiftActions.GetFactoryShiftInfoSuccess(res)),
          catchError(() => {
            return of(new FactoryShiftActions.GetFactoryShiftInfoFailure());
          }),
        ),
      ),
    ),
  );

  addEditWorkingHours$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TFactoryShiftInfoActionType.AddEditWorkingHours),
      switchMap((action: FactoryShiftActions.AddEditWorkingHours) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.plani8DataService.addEditFactoryWorkingHours(action.factoryId, action.payload).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          map((res) => {
            this.appNotificationUtilClass.presentToast('Edit working hours successful');
            return new FactoryShiftActions.GetFactoryShiftInfo(action.factoryId, {
              organization_hierarchy_filter: action.payload.organization_hierarchy_filter,
              shift_id: action.payload.shift_id,
            });
          }),
          catchError(() => {
            return of(new FactoryShiftActions.AddEditWorkingHoursFailure());
          }),
        ),
      ),
    ),
  );

  resetWorkingHours$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TFactoryShiftInfoActionType.ResetWorkingHours),
      switchMap((action: FactoryShiftActions.ResetWorkingHours) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.plani8DataService.resetFactoryWorkingHours(action.factoryId, action.payload).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          map((res) => {
            this.appNotificationUtilClass.presentToast('Reset working hours successful');
            return new FactoryShiftActions.GetFactoryShiftInfo(action.factoryId, {
              organization_hierarchy_filter: action.payload.organization_hierarchy_filter,
              shift_id: action.payload.shift_id,
            });
          }),
          catchError(() => {
            return of(new FactoryShiftActions.ResetWorkingHoursFailure());
          }),
        ),
      ),
    ),
  );

  getFactoryHolidaysInfo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TFactoryShiftInfoActionType.GetFactoryHolidaysInfo),
      switchMap((action: FactoryShiftActions.GetFactoryHolidayInfo) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.plani8DataService.fetchFactoryHolidays(action.factoryId).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          map((res) => new FactoryShiftActions.GetFactoryHolidayInfoSuccess(res)),
          catchError(() => {
            return of(new FactoryShiftActions.GetFactoryHolidayInfoFailure());
          }),
        ),
      ),
    ),
  );

  addEditFactoryHolidays$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TFactoryShiftInfoActionType.AddEditFactoryHoliday),
      switchMap((action: FactoryShiftActions.AddEditFactoryHoliday) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.plani8DataService.addEditFactoryHolidays(action.factoryId, action.payload).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          map((res) => new FactoryShiftActions.GetFactoryHolidayInfo(action.factoryId)),
          catchError(() => {
            return of(new FactoryShiftActions.AddEditWorkingHoursFailure());
          }),
        ),
      ),
    ),
  );

  deleteFactoryHoliday$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TFactoryShiftInfoActionType.DeleteFactoryHoliday),
      switchMap((action: FactoryShiftActions.DeleteFactoryHoliday) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.plani8DataService.deleteFactoryHolidays(action.factoryId, action.payload).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          map((res) => new FactoryShiftActions.GetFactoryHolidayInfo(action.factoryId)),
          catchError(() => {
            return of(new FactoryShiftActions.DeleteFactoryHolidayFailure());
          }),
        ),
      ),
    ),
  );

  addEditOperatorCount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TFactoryShiftInfoActionType.AddEditOperatorCount),
      switchMap((action: FactoryShiftActions.AddEditOperatorCount) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.plani8DataService.addEditFactoryOperatorCount(action.factoryId, action.payload).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          map((res) => {
            this.appNotificationUtilClass.presentToast('Edit operator count successful');
            return new FactoryShiftActions.GetFactoryShiftInfo(action.factoryId, {
              organization_hierarchy_filter: action.payload.organization_hierarchy_filter,
              shift_id: action.shiftId,
            });
          }),
          catchError(() => {
            return of(new FactoryShiftActions.AddEditOperatorCountFailure());
          }),
        ),
      ),
    ),
  );

  resetOperatorCount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TFactoryShiftInfoActionType.ResetOperatorCount),
      switchMap((action: FactoryShiftActions.ResetOperatorCount) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.plani8DataService.resetFactoryOperatorCount(action.factoryId, action.payload).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          map(() => {
            this.appNotificationUtilClass.presentToast('Reset operator count successful');
            return new FactoryShiftActions.GetFactoryShiftInfo(action.factoryId, {
              organization_hierarchy_filter: action.payload.organization_hierarchy_filter,
              shift_id: action.payload.shift_id,
            });
          }),
          catchError(() => {
            return of(new FactoryShiftActions.ResetOperatorCountFailure());
          }),
        ),
      ),
    ),
  );

  updateDefaultWorkforceCountRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TFactoryShiftInfoActionType.UpdateDefaultWorkforceCountRequest),
      switchMap((action: FactoryShiftActions.UpdateDefaultWorkforceCountRequest) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.plani8DataService.updateDefaultWorkforceCountRequest(action.payload).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          map(() => {
            this.appNotificationUtilClass.presentToast('Update Default Workforce Count Request successful');
            return new FactoryShiftActions.GetFactoryShiftInfo(action.payload.factoryId, {
              organization_hierarchy_filter: action.payload.organizationHierarchyFilter,
              shift_id: action.payload.shift_id,
            });
          }),
          catchError(() => {
            return of(new FactoryShiftActions.UpdateDefaultWorkforceCountRequestFailure());
          }),
        ),
      ),
    ),
  );

  updateDefaultWorkingHours$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TFactoryShiftInfoActionType.UpdateDefaultWorkingHoursRequest),
      switchMap((action: FactoryShiftActions.UpdateDefaultWorkingHoursRequest) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.plani8DataService.updateDefaultWorkingHours(action.payload, action.factoryId).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          map(() => {
            this.appNotificationUtilClass.presentToast('Update Default Working Hours Request successful');
            return new FactoryShiftActions.GetFactoryShiftInfo(action.factoryId, {
              organization_hierarchy_filter: action.payload.organization_hierarchy_filter,
              shift_id: action.payload.shift_id,
            });
          }),
          catchError(() => {
            return of(new FactoryShiftActions.UpdateDefaultWorkingHoursRequestSuccess());
          }),
        ),
      ),
    ),
  );

  addEditWorkForceCount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TFactoryShiftInfoActionType.AddEditWorkForceCount),
      switchMap((action: FactoryShiftActions.AddEditWorkForceCount) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.plani8DataService.addEditWorkForceCount(action.payload, action.factoryId).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          map(() => {
            this.appNotificationUtilClass.presentToast('Edit Workforce count successful');
            return new FactoryShiftActions.GetFactoryShiftInfo(action.factoryId, {
              organization_hierarchy_filter: action.payload.organizationHierarchyFilter,
              shift_id: action.payload.shiftId,
            });
          }),
          catchError(() => {
            return of(new FactoryShiftActions.AddEditWorkForceCountFailure());
          }),
        ),
      ),
    ),
  );

  workForceResetToDefault$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TFactoryShiftInfoActionType.WorkForceResetToDefault),
      switchMap((action: FactoryShiftActions.WorkForceResetToDefault) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.plani8DataService.workForceResetToDefault(action.payload, action.factoryId).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          map(() => {
            this.appNotificationUtilClass.presentToast('Reset WorkForce successful');
            return new FactoryShiftActions.GetFactoryShiftInfo(action.factoryId, {
              organization_hierarchy_filter: action.payload.organizationHierarchyFilter,
              shift_id: action.payload.shift_id,
            });
          }),
          catchError(() => {
            return of(new FactoryShiftActions.WorkForceResetToDefaultFailure());
          }),
        ),
      ),
    ),
  );
}
